import { useCallback, useEffect, useState } from 'react';

/**
 * This hook debounces a state value that might be changing often.
 * @param value The value to debounce.
 * @param delay The delay before updating the value.
 */
export function useDebounce<T>(value: T, delay = 250) {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const [debouncing, setDebouncing] = useState(false);

  const syncValue = useCallback(() => {
    setDebouncedValue(value);
    setDebouncing(false);
  }, [value]);

  useEffect(() => {
    if (delay <= 0) {
      syncValue();
      return;
    }

    setDebouncing(true);
    const timer = setTimeout(syncValue, delay);

    return () => {
      clearTimeout(timer);
      setDebouncing(false);
    };
  }, [syncValue, delay]);

  return { value: debouncedValue, debouncing };
}
